import { errorMessage } from "@lumora/bridge";
import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { client } from "../client.js";

/** Matches the server's minimum for owner passwords. */
export const OWNER_ACCEPT_MIN_PASSWORD = 12;

/** Target of owner invite and password-reset emails: `/#/owner/accept?token=…`. */
export function OwnerAcceptPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() ?? "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="site-signin" data-page="owner-accept">
        <h1>Link not valid</h1>
        <p>This password link is missing its token. Ask for a new one.</p>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await client.acceptOwnerInvite({ token, password });
      setPassword("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Could not set your password. The link may have expired."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="site-signin" data-page="owner-accept">
      <h1>Set your password</h1>
      <form onSubmit={onSubmit}>
        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            minLength={OWNER_ACCEPT_MIN_PASSWORD}
            required
          />
        </label>
        {error ? <p role="alert">{error}</p> : null}
        <button type="submit" disabled={loading || password.length < OWNER_ACCEPT_MIN_PASSWORD}>
          {loading ? "Saving…" : "Set password and sign in"}
        </button>
      </form>
    </div>
  );
}
